import {
    UnitHelpers,
    UnitType,
    UnitMode,
    CoordinateFormat,
    CoordinateFormatKey,
    CoordinateError,
    CoordinateErrorCode,
    CompassDirection,
} from 'ihunter-common';
import { useI18n } from 'vue-i18n';
import { useI18n as usePathfinderI18n } from './useI18n';

const UNITS_FOR_TYPE = Object.freeze({
    [UnitType.Area]: { [UnitMode.Metric]: 'ha', [UnitMode.Imperial]: 'ac' },
    [UnitType.DistanceKm]: { [UnitMode.Metric]: 'km', [UnitMode.Imperial]: 'mi' },
    [UnitType.DistanceMeters]: { [UnitMode.Metric]: 'm', [UnitMode.Imperial]: 'yd' },
    [UnitType.DistanceFeet]: { [UnitMode.Metric]: 'm', [UnitMode.Imperial]: 'feet' },
    [UnitType.Elevation]: { [UnitMode.Metric]: 'm', [UnitMode.Imperial]: 'feet' },
    [UnitType.Precipitation]: { [UnitMode.Metric]: 'mm', [UnitMode.Imperial]: 'inch' },
    [UnitType.Pressure]: { [UnitMode.Metric]: 'kpa', [UnitMode.Imperial]: 'hpa' },
    [UnitType.SpeedKph]: { [UnitMode.Metric]: 'kph', [UnitMode.Imperial]: 'mph' },
    [UnitType.SpeedMps]: { [UnitMode.Metric]: 'mps', [UnitMode.Imperial]: 'yds' },
    [UnitType.Temperature]: { [UnitMode.Metric]: 'celsius', [UnitMode.Imperial]: 'fahrenheit' },
});

const MAX_DECIMAL_PLACES = Object.freeze({
    [UnitType.Area]: 2,
    [UnitType.Coordinates]: 6,
    [UnitType.DistanceMeters]: 2,
    [UnitType.DistanceFeet]: 2,
    [UnitType.DistanceKm]: 2,
    [UnitType.Precipitation]: 1,
    [UnitType.Pressure]: 1,
    [UnitType.SpeedKph]: 2,
    [UnitType.SpeedMps]: 2,
});

const UNKNOWN_UNIT_VALUE = '-';

const useUnits = () => {
    const { t, n, locale } = useI18n();
    const { getTimeFromTimestamp } = usePathfinderI18n();

    /**
     * Gets units for the specified uint type and mode (metric or imperial).
     * @param {UnitType} unitType the type of units
     * @param {UnitMode} unitMode whether to format the value in metric or imperial
     * @returns {string} the units.
     */
    const getUnits = (unitType, unitMode) => {
        return UNITS_FOR_TYPE[unitType]?.[unitMode];
    };

    /**
     * Formats a distance in the units that suit its size: km (miles in imperial) from 1 km (1 mile) up,
     * otherwise meters (yards or feet in imperial).
     * @param {number} meters the distance in meters
     * @param {UnitMode} unitMode whether to format the distance in metric or imperial
     * @param {boolean} useYards (optional) in imperial, whether distances under 1 mile are in yards (true) or feet (false). Defaults to false (feet).
     * @returns {string} the converted distance, formatted with the appropriate units.
     */
    const formatDistance = (meters, unitMode, useYards = false) => {
        const km = Number(meters) / 1000;
        const isLong = unitMode === UnitMode.Imperial ? UnitHelpers.KmToMiles(km) >= 1 : km >= 1;
        if (isLong) {
            return formatUnitValue(km, UnitType.DistanceKm, unitMode);
        }
        const unitType = unitMode === UnitMode.Imperial && !useYards ? UnitType.DistanceFeet : UnitType.DistanceMeters;
        return formatUnitValue(meters, unitType, unitMode);
    };

    const convertValue = (value, unitType, unitMode, maxDecimalPlaces = getMaxDecimalPlaces(unitType)) => {
        if (!Object.values(UnitType).includes(unitType)) {
            throw new Error(`Invalid unitType ${unitType}`);
        }
        if (!Object.values(UnitMode).includes(unitMode)) {
            throw new Error(`Invalid unitMode ${unitMode}`);
        }

        if (!isValidUnitValue(value)) {
            return UNKNOWN_UNIT_VALUE;
        }

        let convertedValue = UnitHelpers.ConvertValue(value, unitType, unitMode, maxDecimalPlaces);
        if (isValidUnitValue(convertedValue)) {
            if (unitType === UnitType.Time) {
                convertedValue = getTimeFromTimestamp(convertedValue);
            } else if (unitType === UnitType.Direction) {
                convertedValue = t(`common.compass_direction.${convertedValue}`);
            } else if (unitType === UnitType.MoonPhase) {
                convertedValue = t(`common.moon_phase.${convertedValue}`);
            } else if (!isNaN(Number(convertedValue))) {
                convertedValue = formatNumber(convertedValue, maxDecimalPlaces);
            }
            return convertedValue;
        }
        return UNKNOWN_UNIT_VALUE;
    };

    /**
     * Formats the provided value to the specified type of units and mode (metric or imperial).
     * @param {(string|number)} value the value to convert
     * @param {UnitType} unitType the type of units
     * @param {UnitMode} unitMode whether to format the value in metric or imperial
     * @param {number} maxDecimalPlaces (optional) the number of decimal places to show. Defaults to the unit type's decimal places.
     * @returns {string} the converted value in the specified unit mode, and formatted with the appropriate units.
     */
    const formatUnitValue = (value, unitType, unitMode, maxDecimalPlaces) => {
        const convertedValue = convertValue(value, unitType, unitMode, maxDecimalPlaces);
        if (isValidUnitValue(convertedValue)) {
            const unitKey = UNITS_FOR_TYPE[unitType]?.[unitMode];
            const units = unitKey ? t(`common.units.${unitKey}`) : '';
            return `${convertedValue} ${units}`.trim();
        }
        return UNKNOWN_UNIT_VALUE;
    };

    /**
     * Formats a number according to the current locale with specific decimal places.
     * @param {number} val - The numeric value to format.
     * @param {number} decimals - The exact number of decimal places to show.
     * @returns {string} The localized number string.
     */
    const formatNumber = (val, decimals) => {
        if (val === null || val === undefined || val === '' || isNaN(Number(val))) {
            return UNKNOWN_UNIT_VALUE;
        }
        return n(val, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    };

    const isValidUnitValue = (value) => {
        return value !== undefined && value !== null && value !== UNKNOWN_UNIT_VALUE && value !== '';
    };

    const getMaxDecimalPlaces = (unitType) => {
        return MAX_DECIMAL_PLACES[unitType] || 0;
    };

    const CoordinateLabels = Object.freeze([
        { label: 'common.coordinates.latlong.label', value: CoordinateFormat.LatLong },
        { label: 'common.coordinates.dms.label', value: CoordinateFormat.DMS },
        { label: 'common.coordinates.mgrs.label', value: CoordinateFormat.MGRS },
        { label: 'common.coordinates.utm.label', value: CoordinateFormat.UTM },
    ]);

    /**
     * Gets the localized label for the specified coordinate format.
     * @param {CoordinateFormat} coordinateFormat
     * @returns {string} the localized label for the coordinate format
     */
    const getCoordinatesLabel = (coordinateFormat) => {
        const key = CoordinateFormatKey[coordinateFormat];
        return t(`common.coordinates.${key}.label`);
    };

    /**
     * Formats the provided coordinates to the specified format, based on the current locale.
     * @param {{lat: number, lng: number}} coordinates - the coordinates to format.
     * @param {CoordinateFormat} coordinateFormat - the format to apply (LatLong, DMS, MGRS, or UTM)
     * @returns {string} the formatted coordinates
     */
    const formatCoordinates = (coordinates, coordinateFormat) => {
        // Note: 0 is a valid latitude/longitude, so check for missing values rather than falsy ones
        const isInvalid = (value) => value === undefined || value === null || value === '' || isNaN(Number(value));
        if (isInvalid(coordinates?.lat) || isInvalid(coordinates?.lng)) {
            return null;
        }

        try {
            switch (coordinateFormat) {
                case CoordinateFormat.LatLong:
                    // e.g. EN: 42.644915, -116.692801
                    // e.g. FR: 42,644915; -116,692801
                    return formatLatLngCoordinates(coordinates);
                case CoordinateFormat.DMS:
                    // e.g. EN: 42° 38' 41.694" N, 116° 41' 34.084" W
                    // e.g. FR: 42° 38' 41,694" N, 116° 41' 34,084" O
                    return formatDmsCoordinates(coordinates);
                case CoordinateFormat.MGRS:
                    // MGRS has a universal standard (no localization needed)
                    // e.g. 11TNH 25183 21430
                    return UnitHelpers.LatLngToMGRS(coordinates);
                case CoordinateFormat.UTM:
                    // UTM has a universal standard (no localization needed)
                    // e.g. 11T 525183 4721430
                    return UnitHelpers.LatLngToUTM(coordinates);
            }
        } catch (error) {
            console.error(`formatCoordinates failed`, error);
        }
        return formatLatLngCoordinates(coordinates);
    };

    /**
     * Formats the provided coordinates to DMS (Degrees, Minutes, Seconds).
     * @param {{lat: number, lng: number}} coordinates - the coordinates to format.
     * @returns {string} the formatted DMS string
     */
    const formatLatLngCoordinates = (coordinates) => {
        const maxDecimalPlaces = getMaxDecimalPlaces(UnitType.Coordinates);
        const lat = formatNumber(coordinates.lat, maxDecimalPlaces);
        const lng = formatNumber(coordinates.lng, maxDecimalPlaces);
        return t('common.coordinates.latlong.format', { lat, lng });
    };

    /**
     * Formats the provided coordinates to DMS (Degrees, Minutes, Seconds).
     * @param {{lat: number, lng: number}} coordinates - the coordinates to format.
     * @returns {string} the formatted DMS string
     */
    const formatDmsCoordinates = (coordinates) => {
        const latDms = getDmsParts(coordinates.lat, true);
        const lngDms = getDmsParts(coordinates.lng, false);
        return t('common.coordinates.dms.format', {
            latD: latDms.d,
            latM: latDms.m,
            latS: latDms.s,
            latDir: latDms.dir,
            lngD: lngDms.d,
            lngM: lngDms.m,
            lngS: lngDms.s,
            lngDir: lngDms.dir,
        });
    };

    /**
     * Extracts DMS components and localized direction for a given coordinate.
     * @param {number} val - The coordinate value (latitude or longitude).
     * @param {boolean} isLat - True if the value is latitude, false if longitude.
     * @returns {{ d: number, m: number, s: string, dir: string }} Object with DMS parts and localized direction.
     */
    const getDmsParts = (val, isLat) => {
        const { d, m, s } = UnitHelpers.DegreesToDMS(val);
        let direction = '';
        if (isLat) {
            direction = val >= 0 ? CompassDirection.North : CompassDirection.South;
        } else {
            direction = val >= 0 ? CompassDirection.East : CompassDirection.West;
        }
        return { d, m, s: formatNumber(s, 3), dir: t(`common.compass_direction.${direction}`) };
    };

    /**
     * Parses a raw string into a LatLng object based on the expected format and the current locale.
     * Throws an error if invalid.
     * @param {string} coordinateStr - The coordinate string.
     * @param {string} coordinateFormat - The expected format to parse against.
     * @returns {{lat: number, lng: number} | null} The parsed coordinate object.
     * @throws {CoordinateError} if parsing fails
     */
    const parseCoordinates = (coordinateStr, coordinateFormat) => {
        try {
            UnitHelpers.ThrowIfBlankCoordinate(coordinateStr);
            coordinateStr = coordinateStr.trim();
            switch (coordinateFormat) {
                case CoordinateFormat.LatLong:
                    return latLngStringToCoordinates(coordinateStr);
                case CoordinateFormat.DMS:
                    return dmsStringToCoordinates(coordinateStr);

                case CoordinateFormat.MGRS:
                    return UnitHelpers.MGRSToLatLng(coordinateStr);

                case CoordinateFormat.UTM:
                    return UnitHelpers.UTMToLatLng(coordinateStr);

                default:
                    return null;
            }
        } catch (error) {
            if ((!error) instanceof CoordinateError) {
                throw new CoordinateError(CoordinateErrorCode.Unknown, error?.message);
            } else {
                throw error;
            }
        }
    };

    /**
     * Safely parses parse a raw string into a LatLng object, returning null if invalid.
     * @param {string} coordinateStr - The coordinate string.
     * @param {string} coordinateFormat - The expected format to parse against.
     * @returns {{lat: number, lng: number} | null} The parsed coordinate object, or null if invalid.
     */
    const tryParseCoordinates = (coordinateStr, coordinateFormat) => {
        try {
            return parseCoordinates(coordinateStr, coordinateFormat);
        } catch (error) {
            //ignore
        }
        return null;
    };

    const latLngStringToCoordinates = (latLngStr) => {
        const latLngRegex = /^(-?\d+(?:[.,]\d+)?)?\s*([;,]|\s+)\s*(-?\d+(?:[.,]\d+)?)?$/i;
        const parts = latLngStr.trim().match(latLngRegex);

        if (parts) {
            const latStr = parts[1];
            const separator = parts[2];
            const lngStr = parts[3];

            const lat = tryParseLocalizedFloat(latStr);
            UnitHelpers.ThrowIfInvalidLatitude(lat);

            const lng = tryParseLocalizedFloat(lngStr);
            UnitHelpers.ThrowIfInvalidLongitude(lng);

            if (!separator.match(/[;,]/)) {
                throw new CoordinateError(CoordinateErrorCode.InvalidFormat);
            }
            return { lat, lng, original: { str: latLngStr, parts } };
        }
        throw new CoordinateError(CoordinateErrorCode.InvalidFormat);
    };

    /**
     * Shared utility to build the localized DMS Regular Expression.
     * Captures degrees, minutes, seconds (with localized decimals), and direction.
     *
     * @param {Function} t - i18n translation function
     * @returns {RegExp}
     */
    const getLocalizedDmsRegex = () => {
        const dirN = t('common.compass_direction.north');
        const dirS = t('common.compass_direction.south');
        const dirE = t('common.compass_direction.east');
        const dirW = t('common.compass_direction.west');

        // Regex looks for: Digits -> NonDigits -> Digits -> NonDigits -> Digits(with decimals) -> NonDigits -> Localized Direction Letter
        // Includes standard NSEW as fallbacks, just in case.
        return new RegExp(`(\\d+)\\D+(\\d+)\\D+([\\d.,]+)\\D+([${dirN}${dirS}${dirE}${dirW}])`, 'gi');
    };

    /**
     * Shared utility to parse an array of regex matches into a decimal value and direction.
     *
     * @param {Array} regexParts - Array of regex matches [fullMatch, deg, min, sec, dir]
     * @returns {{ val: number, dir: string }}
     */
    const extractDecimalFromRegex = (regexParts) => {
        const d = parseInt(regexParts[1] || 0, 10);
        const m = parseInt(regexParts[2] || 0, 10);
        const s = regexParts[3] ? parseStringAsFloat(regexParts[3]) : 0;
        const dir = regexParts[4] ? regexParts[4].toUpperCase() : '';

        let decimalVal = Math.abs(d) + m / 60 + s / 3600;

        // Check both localized and standard English directions for negatives
        const isNegativeDirection =
            dir === t('common.compass_direction.south') || dir === t('common.compass_direction.west') || dir === 'S' || dir === 'W';

        if (isNegativeDirection || d < 0 || Object.is(d, -0)) {
            decimalVal = -decimalVal;
        }

        return { val: decimalVal, dir };
    };

    const dmsStringToCoordinates = (dmsStr) => {
        const parts = [];
        let match;
        const regex = getLocalizedDmsRegex();
        while ((match = regex.exec(dmsStr)) !== null) {
            parts.push(match);
        }

        if (parts.length >= 2) {
            const p1 = extractDecimalFromRegex(parts[0]);
            const p2 = extractDecimalFromRegex(parts[1]);

            const dirN = t('common.compass_direction.north');
            const dirS = t('common.compass_direction.south');

            // Figure out which is Lat and which is Lng based on the matched direction letter
            let lat, lng;
            if (p1.dir === dirN || p1.dir === dirS || p1.dir === 'N' || p1.dir === 'S') {
                lat = p1.val;
                lng = p2.val;
            } else {
                lat = p2.val;
                lng = p1.val;
            }
            UnitHelpers.ThrowIfInvalidLatitude(lat);
            UnitHelpers.ThrowIfInvalidLongitude(lng);
            return { lat, lng, original: { str: dmsStr, parts } };
        }
        throw new CoordinateError(CoordinateErrorCode.InvalidFormat);
    };

    /**
     * Parses a DMS axis coordinate string or number into its component DMS parts.
     *
     * @param {string | number} input - The raw DMS axis string or Decimal Degree number (latitude or longitude)
     * @returns {{ deg: string, min: string, sec: string }}
     */
    const dmsCoordinateAxisToParts = (input) => {
        const parts = { deg: '', min: '', sec: '' };

        // Handle empty/null/undefined cases gracefully
        if (input === null || input === undefined || input === '') return parts;

        // 1. Handle Number Input (Mathematical conversion from Decimal Degrees to DMS)
        if (typeof input === 'number') {
            const absolute = Math.abs(input);
            let degrees = Math.floor(absolute);

            const minutesNotTruncated = (absolute - degrees) * 60;
            let minutes = Math.floor(minutesNotTruncated);

            let seconds = (minutesNotTruncated - minutes) * 60;

            // Round seconds to 4 decimal places safely using Math.round
            seconds = Math.round(seconds * 10000) / 10000;

            // If seconds rounded up to 60, carry over to minutes
            if (seconds >= 60) {
                seconds = 0;
                minutes += 1;
            }

            // If minutes carried over to 60, carry over to degrees
            if (minutes >= 60) {
                minutes = 0;
                degrees += 1;
            }

            // Apply negative sign if original input was less than 0
            // Object.is handles the rare edge case of -0
            const isNegative = input < 0 || Object.is(input, -0);
            parts.deg = isNegative ? `-${degrees}` : `${degrees}`;

            // Output '0' for minutes if there are seconds to maintain valid formatting
            parts.min = minutes > 0 || seconds > 0 ? `${minutes}` : '';
            parts.sec = seconds > 0 ? `${seconds}` : '';

            return parts;
        }

        // 2. Handle String Input
        const str = String(input).trim();
        const regex = getLocalizedDmsRegex();
        const match = str.match(regex);

        if (match) {
            // We reuse the central parsing logic to get the final decimal value
            // Then convert that clean decimal back into parts.
            // This guarantees consistency between the two parsing methods.
            const { val } = extractDecimalFromRegex(match);

            // Pass the resulting number back into this same function to format the output object reliably
            return dmsAxisToParts(val);
        }

        return parts;
    };

    /**
     * Formats DMS parts into a fully qualified axis string with directional letters.
     * Assumes Canadian default: Longitude is Westing (positive = West, negative = East)
     *
     * @param {string} deg - Degrees
     * @param {string} min - Minutes
     * @param {string} sec - Seconds
     * @param {boolean} isLatitude - True if this is the Latitude axis, False for Longitude
     * @returns {string} Fully formatted axis string e.g. "44° 38' 56\" N"
     */
    const dmsCoordinatePartsToAxis = (deg, min, sec, isLatitude) => {
        if (!deg && !min && !sec) return '';

        let isNegative = false;
        let cleanDeg = deg || '';

        if (cleanDeg.startsWith('-')) {
            isNegative = true;
            cleanDeg = cleanDeg.substring(1);
        }

        const parts = [cleanDeg ? `${cleanDeg}°` : '', min ? `${min}'` : '', sec ? `${sec}"` : ''].filter(Boolean).join(' ');

        if (!parts) return '';

        let direction = '';
        if (isLatitude) {
            direction = isNegative ? CompassDirection.South : CompassDirection.North;
        } else {
            direction = isNegative ? CompassDirection.East : CompassDirection.West;
        }

        return `${parts} ${t(`common.compass_direction.${direction}`)}`;
    };

    // Helper to dynamically get the decimal separator for the current locale
    const getDecimalSeparator = () => {
        return new Intl.NumberFormat(locale.value).format(1.1).replace(/1/g, '');
    };

    // Helper to safely parse localized floats (e.g. converting "42,123" back to 42.123)
    const parseStringAsFloat = (str) => {
        // Allow both the localized separator and the standard period for maximum leniency
        const decimalSep = getDecimalSeparator();
        const normalized = str.replace(decimalSep, '.').replace(',', '.');
        return parseFloat(normalized);
    };

    const tryParseLocalizedFloat = (str) => {
        if (typeof str === 'string' && str.trim() !== '') {
            return parseStringAsFloat(str);
        } else if (!isNaN(Number(str))) {
            // If it's already a number or numeric string, parse it directly
            return parseFloat(str);
        }
        return NaN;
    };

    return {
        formatUnitValue,
        getUnits,
        formatDistance,
        isValidUnitValue,

        getCoordinatesLabel,
        formatCoordinates,
        parseCoordinates,
        tryParseCoordinates,
        dmsCoordinateAxisToParts,
        dmsCoordinatePartsToAxis,
        tryParseLocalizedFloat,

        CoordinateLabels,
        UnknownUnitValue: UNKNOWN_UNIT_VALUE,
    };
};

export { useUnits as default, useUnits };
